import React, { useState, useEffect } from "react";
import api from "../utils/axiosConfig";
import "../css/travelForm.css";
import localforage from "localforage";
import Dashboard from "./Dashboard";
import { useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL;

const RequestTravel = () => {
  const [formData, setFormData] = useState({
    userId: "",
    email: "",
    department: "",
    age: "",
    employeePhoneNumber: "",
    requestType: "",
    travelLegs: [{ travelDate: "", fromLocation: "", toLocation: "" }],
    accommodations: [{ startDate: "", endDate: "", location: "" }],
    purposeOfTravel: "",
    eventDetails: "",
    eventName: "",
    travelInstructedBy: "",
    eventLocation: "",
    remarks: "",
  });

  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("self"); // "self" | "group"
  const [team, setTeam] = useState([]);
  const emptyLeg = () => ({
    travelDate: "",
    fromLocation: "",
    toLocation: "",
    accommodationPlace: "",
    accommodationStartDate: "",
    accommodationEndDate: "",
  });
  const [itineraries, setItineraries] = useState([
    { memberIds: [], legs: [emptyLeg()], eventDetails: "" },
  ]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  // Fetch userId and email from localforage on component mount
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userId = await localforage.getItem("ID");
        const email = await localforage.getItem("email"); // Fetch email from localforage

        if (userId && email) {
          setFormData((prevData) => ({
            ...prevData,
            userId,
            email, // Set email to formData
          }));
        } else {
          console.error("User ID or Email not found in localforage");
          setError("User ID or Email not found. Please log in again.");
        }
      } catch (err) {
        console.error("Error fetching user data:", err);
      }
    };

    fetchUserData();
    setDepartments([
      "Soul (Field)",
      "APEX",
      "Campaign",
      "DATA",
      "Directors Team",
      "DMC",
      "Admin",
      "HR",
      "Media",
      "PMU",
      "Political Support Unit",
      "Research",
    ]);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // ---- Travel legs (repeatable) ----
  const handleLegChange = (index, field, value) => {
    setFormData((prev) => {
      const travelLegs = prev.travelLegs.map((leg, i) =>
        i === index ? { ...leg, [field]: value } : leg
      );
      return { ...prev, travelLegs };
    });
  };
  const addLeg = () =>
    setFormData((prev) => ({
      ...prev,
      travelLegs: [
        ...prev.travelLegs,
        { travelDate: "", fromLocation: "", toLocation: "" },
      ],
    }));
  const removeLeg = (index) =>
    setFormData((prev) => ({
      ...prev,
      travelLegs:
        prev.travelLegs.length > 1
          ? prev.travelLegs.filter((_, i) => i !== index)
          : prev.travelLegs,
    }));

  // ---- Accommodation stays (repeatable) ----
  const handleStayChange = (index, field, value) => {
    setFormData((prev) => {
      const accommodations = prev.accommodations.map((a, i) =>
        i === index ? { ...a, [field]: value } : a
      );
      return { ...prev, accommodations };
    });
  };
  const addStay = () =>
    setFormData((prev) => ({
      ...prev,
      accommodations: [
        ...prev.accommodations,
        { startDate: "", endDate: "", location: "" },
      ],
    }));
  const removeStay = (index) =>
    setFormData((prev) => ({
      ...prev,
      accommodations:
        prev.accommodations.length > 1
          ? prev.accommodations.filter((_, i) => i !== index)
          : prev.accommodations,
    }));

  useEffect(() => {
    (async () => {
      try {
        const token = await localforage.getItem("token");
        const res = await api.get(`${API_URL}/auth/my-team`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setTeam(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        setTeam([]);
      }
    })();
  }, []);

  // Repeatable itinerary blocks: each block has its own members, legs and event
  // details, so one request can cover several trips for several teams.
  const addItinerary = () =>
    setItineraries((prev) => [
      ...prev,
      { memberIds: [], legs: [emptyLeg()], eventDetails: "" },
    ]);
  const removeItinerary = (bi) =>
    setItineraries((prev) =>
      prev.length > 1 ? prev.filter((_, i) => i !== bi) : prev
    );
  const toggleMemberIn = (bi, id) =>
    setItineraries((prev) =>
      prev.map((b, i) =>
        i === bi
          ? {
              ...b,
              memberIds: b.memberIds.includes(id)
                ? b.memberIds.filter((x) => x !== id)
                : [...b.memberIds, id],
            }
          : b
      )
    );
  const addLegIn = (bi) =>
    setItineraries((prev) =>
      prev.map((b, i) => (i === bi ? { ...b, legs: [...b.legs, emptyLeg()] } : b))
    );
  const removeLegIn = (bi, li) =>
    setItineraries((prev) =>
      prev.map((b, i) =>
        i === bi
          ? { ...b, legs: b.legs.length > 1 ? b.legs.filter((_, x) => x !== li) : b.legs }
          : b
      )
    );
  const legChangeIn = (bi, li, field, value) =>
    setItineraries((prev) =>
      prev.map((b, i) =>
        i === bi
          ? { ...b, legs: b.legs.map((l, x) => (x === li ? { ...l, [field]: value } : l)) }
          : b
      )
    );
  const eventDetailsIn = (bi, value) =>
    setItineraries((prev) =>
      prev.map((b, i) => (i === bi ? { ...b, eventDetails: value } : b))
    );

  const handleGroupSubmit = async () => {
    setError(null);
    setSuccess(false);
    if (!formData.requestType) {
      setError("Select a request type.");
      return;
    }
    if (itineraries.some((b) => b.memberIds.length === 0)) {
      setError("Every itinerary needs at least one team member selected.");
      return;
    }
    setLoading(true);
    try {
      const token = await localforage.getItem("token");
      const payloadItineraries = itineraries.map((b) => ({
        members: team
          .filter((u) => b.memberIds.includes(u._id))
          .map((u) => ({ userId: u._id, email: u.email })),
        travelLegs: b.legs,
        eventDetails: b.eventDetails,
      }));
      const res = await api.post(
        `${API_URL}/travel/group-travel-record`,
        {
          itineraries: payloadItineraries,
          requestType: formData.requestType,
          purposeOfTravel: formData.purposeOfTravel,
          eventName: formData.eventName,
          eventLocation: formData.eventLocation,
          remarks: formData.remarks,
          travelInstructedBy: formData.travelInstructedBy,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { created = [], failed = [] } = res.data || {};
      let msg = `Group travel submitted: ${created.length} record(s) across ${itineraries.length} itinerary(ies).`;
      if (failed.length) {
        msg += ` ${failed.length} failed: ${failed
          .map((f) => f.email)
          .join(", ")} (check they exist in Employee Data).`;
      }
      setSuccess(msg);
      setItineraries([{ memberIds: [], legs: [emptyLeg()], eventDetails: "" }]);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to submit group request.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mode === "group") {
      await handleGroupSubmit();
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const token = await localforage.getItem("token");
      if (!formData.userId) {
        throw new Error("Missing userId in the request body");
      }

      const response = await api.post(
        `${API_URL}/travel/travel-record`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const { travelCode } = response.data;
      setSuccess(`Travel request submitted! Leave Code: ${travelCode}`);

      // Reset the form fields to initial state
      setFormData({
        userId: formData.userId, // Retain userId
        email: formData.email, // Retain email
        department: "",
        age: "",
        employeePhoneNumber: "",
        requestType: "",
        travelLegs: [{ travelDate: "", fromLocation: "", toLocation: "" }],
        accommodations: [{ startDate: "", endDate: "", location: "" }],
        purposeOfTravel: "",
        eventDetails: "",
        eventLocation: "",
        remarks: "",
        phoneNumber: "",
        eventName: "",
        travelInstructedBy: "",
      });

      setTimeout(() => {
        setSuccess(false);
        navigate("/travel-request-user-data");
      }, 3000);
    } catch (error) {
      console.error("Error submitting request:", error);
      setError("Failed to submit request.");
      setTimeout(() => setError(null), 5000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dashboard />
      <div className="travel-form-container">
        <h2>Travel Request Form</h2>
        {success && <p className="success-message">{success}</p>}
        {error && <p className="error-message">{error}</p>}

        <div style={{ display: "flex", gap: "10px", margin: "6px 0 16px", flexWrap: "wrap" }}>
          {[
            { key: "self", label: "For Myself" },
            { key: "group", label: "For My Team (Group)" },
          ].map((m) => (
            <button
              key={m.key}
              type="button"
              onClick={() => setMode(m.key)}
              style={{
                padding: "8px 16px", borderRadius: "8px", fontWeight: 600, cursor: "pointer",
                border: "1px solid " + (mode === m.key ? "#1f6fd0" : "#cfd8e3"),
                background: mode === m.key ? "#1f6fd0" : "#fff",
                color: mode === m.key ? "#fff" : "#1b2b41",
              }}
            >
              {m.label}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="tv-form">
          <style>{`
            .tv-form { width: 100%; }
            .tv-grid {
              display: grid;
              grid-template-columns: repeat(2, minmax(0, 1fr));
              gap: 16px 20px;
              margin-bottom: 16px;
            }
            .tv-field { display: flex; flex-direction: column; }
            .tv-field > label { font-weight: 600; margin-bottom: 6px; color: #1b2b41; font-size: 14px; }
            .tv-field > input,
            .tv-field > select,
            .tv-field > textarea {
              width: 100%; box-sizing: border-box; padding: 9px 11px;
              border: 1px solid #cfd8e3; border-radius: 8px; font-size: 14px; background: #fff;
            }
            .tv-full { grid-column: 1 / -1; }
            .tv-full > textarea { min-height: 84px; resize: vertical; }
            .tv-actions { display: flex; justify-content: flex-end; margin-top: 8px; }
            .tv-repeat {
              border: 1px solid #e3e9f1; border-radius: 12px; padding: 12px 14px;
              background: #fbfcfe; margin-top: 4px;
            }
            .tv-repeat-head {
              display: flex; align-items: center; justify-content: space-between;
              margin-bottom: 10px; font-weight: 700; color: #1b2b41; font-size: 15px;
            }
            .tv-add {
              background: #1f6fd0; color: #fff; border: none; border-radius: 8px;
              padding: 6px 12px; font-size: 13px; font-weight: 600; cursor: pointer;
            }
            .tv-leg {
              display: grid;
              grid-template-columns: 28px repeat(3, minmax(0, 1fr)) 34px;
              gap: 10px 12px; align-items: end; margin-bottom: 10px;
            }
            .tv-leg-no {
              display: flex; align-items: center; justify-content: center;
              height: 38px; border-radius: 8px; background: #eef3fb; color: #1f6fd0;
              font-weight: 700; font-size: 13px;
            }
            .tv-remove {
              height: 38px; border: 1px solid #f0c9c9; background: #fff5f5;
              color: #c0392b; border-radius: 8px; font-size: 18px; line-height: 1;
              cursor: pointer;
            }
            .tv-remove:disabled { opacity: .4; cursor: not-allowed; }
            @media (max-width: 640px) {
              .tv-leg { grid-template-columns: 1fr; }
              .tv-leg-no { width: 38px; }
            }
            .tv-gleg {
              display: flex; flex-wrap: wrap; gap: 10px 12px; align-items: end;
              margin-bottom: 12px; padding-bottom: 12px; border-bottom: 1px dashed #e3e9f1;
            }
            .tv-gleg .tv-field { flex: 1 1 160px; min-width: 150px; }
            .tv-gleg .tv-leg-no { flex: 0 0 28px; }
            .tv-gleg .tv-remove { flex: 0 0 34px; }
            @media (max-width: 640px) { .tv-grid { grid-template-columns: 1fr; } }
          `}</style>

          <div className="tv-grid">
            {mode === "self" && (
              <div className="tv-field">
                <label>Department</label>
                <select name="department" value={formData.department} onChange={handleChange} required={mode === "self"}>
                  <option value="">Select Department</option>
                  {departments.map((dept) => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="tv-field">
              <label>Request Type</label>
              <select name="requestType" value={formData.requestType} onChange={handleChange} required>
                <option value="">Select Request Type</option>
                <option value="Travel + Hotel Accommodation">Travel + Hotel Accommodation</option>
                <option value="Only Travel">Only Travel</option>
                <option value="Only Accommodation">Only Accommodation</option>
              </select>
            </div>
            <div className="tv-field">
              <label>Purpose of Travel</label>
              <select name="purposeOfTravel" value={formData.purposeOfTravel} onChange={handleChange} required>
                <option value="">Select Purpose</option>
                <option value="Event">Event</option>
                <option value="Research">Research</option>
                <option value="Field visit">Field visit</option>
                <option value="Team meeting">Team meeting</option>
                <option value="Field work">Field work</option>
                <option value="New mapping">New mapping</option>
                <option value="Others">Others</option>
              </select>
            </div>
            {mode === "self" && (
              <div className="tv-field">
                <label>Phone Number</label>
                <input
                  type="tel"
                  name="employeePhoneNumber"
                  value={formData.employeePhoneNumber}
                  onChange={handleChange}
                  required={mode === "self"}
                  pattern="[0-9]{10}"
                  title="Enter a 10-digit mobile number"
                  placeholder="10-digit mobile number"
                />
              </div>
            )}
            {mode === "self" && (
              <div className="tv-field">
                <label>Age</label>
                <input type="number" name="age" value={formData.age} onChange={handleChange} required={mode === "self"} placeholder="Enter the Age..." min="1" max="100" />
              </div>
            )}
            <div className="tv-field">
              <label>Event Name</label>
              <input type="text" name="eventName" value={formData.eventName} onChange={handleChange} required placeholder="Enter the Event Name..." />
            </div>
            <div className="tv-field">
              <label>Event Location</label>
              <input type="text" name="eventLocation" value={formData.eventLocation} onChange={handleChange} placeholder="Enter the Event Location..." />
            </div>
            <div className="tv-field">
              <label>Travel Instructed By</label>
              <input type="text" name="travelInstructedBy" value={formData.travelInstructedBy} onChange={handleChange} placeholder="Enter the Travel Instructed By..." />
            </div>

            {mode === "self" && formData.requestType !== "Only Accommodation" && (
              <div className="tv-full tv-repeat">
                <div className="tv-repeat-head">
                  <span>Travel Legs</span>
                  <button type="button" className="tv-add" onClick={addLeg}>
                    + Add leg
                  </button>
                </div>
                {formData.travelLegs.map((leg, i) => (
                  <div className="tv-leg" key={i}>
                    <div className="tv-leg-no">{i + 1}</div>
                    <div className="tv-field">
                      <label>Travel Date &amp; Time</label>
                      <input
                        type="datetime-local"
                        value={leg.travelDate}
                        onChange={(e) => handleLegChange(i, "travelDate", e.target.value)}
                        required={formData.requestType !== "Only Accommodation"}
                      />
                    </div>
                    <div className="tv-field">
                      <label>From Location</label>
                      <input
                        type="text"
                        value={leg.fromLocation}
                        onChange={(e) => handleLegChange(i, "fromLocation", e.target.value)}
                        required={formData.requestType !== "Only Accommodation"}
                        placeholder="Source..."
                      />
                    </div>
                    <div className="tv-field">
                      <label>To Location</label>
                      <input
                        type="text"
                        value={leg.toLocation}
                        onChange={(e) => handleLegChange(i, "toLocation", e.target.value)}
                        required={formData.requestType !== "Only Accommodation"}
                        placeholder="Destination..."
                      />
                    </div>
                    <button
                      type="button"
                      className="tv-remove"
                      onClick={() => removeLeg(i)}
                      disabled={formData.travelLegs.length === 1}
                      title={formData.travelLegs.length === 1 ? "At least one leg" : "Remove leg"}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            {mode === "self" && ["Travel + Hotel Accommodation", "Only Accommodation"].includes(formData.requestType) && (
              <div className="tv-full tv-repeat">
                <div className="tv-repeat-head">
                  <span>Accommodation</span>
                  <button type="button" className="tv-add" onClick={addStay}>
                    + Add stay
                  </button>
                </div>
                {formData.accommodations.map((a, i) => (
                  <div className="tv-leg" key={i}>
                    <div className="tv-leg-no">{i + 1}</div>
                    <div className="tv-field">
                      <label>Start Date &amp; Time</label>
                      <input
                        type="datetime-local"
                        value={a.startDate}
                        onChange={(e) => handleStayChange(i, "startDate", e.target.value)}
                        required
                      />
                    </div>
                    <div className="tv-field">
                      <label>End Date &amp; Time</label>
                      <input
                        type="datetime-local"
                        value={a.endDate}
                        onChange={(e) => handleStayChange(i, "endDate", e.target.value)}
                        required
                      />
                    </div>
                    <div className="tv-field">
                      <label>Location / City</label>
                      <input
                        type="text"
                        value={a.location}
                        onChange={(e) => handleStayChange(i, "location", e.target.value)}
                        placeholder="City (optional)"
                      />
                    </div>
                    <button
                      type="button"
                      className="tv-remove"
                      onClick={() => removeStay(i)}
                      disabled={formData.accommodations.length === 1}
                      title={formData.accommodations.length === 1 ? "At least one stay" : "Remove stay"}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            {mode === "group" && (
              <div className="tv-full">
                {itineraries.map((block, bi) => (
                  <div className="tv-repeat" key={bi} style={{ marginBottom: "16px" }}>
                    <div className="tv-repeat-head">
                      <span>Itinerary {bi + 1}</span>
                      <button
                        type="button"
                        className="tv-remove"
                        onClick={() => removeItinerary(bi)}
                        disabled={itineraries.length === 1}
                        title={itineraries.length === 1 ? "At least one itinerary" : "Remove itinerary"}
                        style={{ width: "auto", padding: "0 12px", fontSize: "13px" }}
                      >
                        Remove
                      </button>
                    </div>

                    <div style={{ marginBottom: "10px" }}>
                      <div style={{ fontWeight: 600, color: "#1b2b41", marginBottom: "6px", fontSize: "14px" }}>
                        Team members ({block.memberIds.length} selected)
                      </div>
                      {team.length === 0 ? (
                        <div style={{ color: "#6a7c93", fontSize: "13px" }}>
                          No team members are mapped to you (no users report to your email).
                        </div>
                      ) : (
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "4px 14px", maxHeight: "160px", overflowY: "auto", border: "1px solid #e3e9f1", borderRadius: "8px", padding: "8px" }}>
                          {team.map((u) => (
                            <label key={u._id} style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", cursor: "pointer" }}>
                              <input type="checkbox" checked={block.memberIds.includes(u._id)} onChange={() => toggleMemberIn(bi, u._id)} />
                              <span>{u.userName || u.email}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="tv-repeat-head" style={{ borderTop: "1px dashed #e3e9f1", paddingTop: "10px" }}>
                      <span>Connecting legs</span>
                      <button type="button" className="tv-add" onClick={() => addLegIn(bi)}>
                        + Add leg
                      </button>
                    </div>
                    {!formData.requestType && (
                      <div style={{ color: "#a12b2b", fontSize: "13px", marginBottom: "8px" }}>
                        Select a Request Type above to choose the fields for each leg.
                      </div>
                    )}
                    {block.legs.map((leg, li) => (
                      <div className="tv-gleg" key={li}>
                        <div className="tv-leg-no">{li + 1}</div>
                        {formData.requestType !== "Only Accommodation" && (
                          <>
                            <div className="tv-field">
                              <label>Travel From</label>
                              <input type="text" value={leg.fromLocation || ""} onChange={(e) => legChangeIn(bi, li, "fromLocation", e.target.value)} placeholder="From..." />
                            </div>
                            <div className="tv-field">
                              <label>Travel To</label>
                              <input type="text" value={leg.toLocation || ""} onChange={(e) => legChangeIn(bi, li, "toLocation", e.target.value)} placeholder="To..." />
                            </div>
                            <div className="tv-field">
                              <label>Travel Date &amp; Time</label>
                              <input type="datetime-local" value={leg.travelDate || ""} onChange={(e) => legChangeIn(bi, li, "travelDate", e.target.value)} />
                            </div>
                          </>
                        )}
                        {["Travel + Hotel Accommodation", "Only Accommodation"].includes(formData.requestType) && (
                          <>
                            <div className="tv-field">
                              <label>Accommodation Place</label>
                              <input type="text" value={leg.accommodationPlace || ""} onChange={(e) => legChangeIn(bi, li, "accommodationPlace", e.target.value)} placeholder="City / hotel..." />
                            </div>
                            <div className="tv-field">
                              <label>Accommodation Start</label>
                              <input type="datetime-local" value={leg.accommodationStartDate || ""} onChange={(e) => legChangeIn(bi, li, "accommodationStartDate", e.target.value)} />
                            </div>
                            <div className="tv-field">
                              <label>Accommodation End</label>
                              <input type="datetime-local" value={leg.accommodationEndDate || ""} onChange={(e) => legChangeIn(bi, li, "accommodationEndDate", e.target.value)} />
                            </div>
                          </>
                        )}
                        <button type="button" className="tv-remove" onClick={() => removeLegIn(bi, li)} disabled={block.legs.length === 1} title={block.legs.length === 1 ? "At least one leg" : "Remove leg"}>
                          ×
                        </button>
                      </div>
                    ))}

                    <div className="tv-field" style={{ marginTop: "10px" }}>
                      <label>Event Details</label>
                      <textarea value={block.eventDetails} onChange={(e) => eventDetailsIn(bi, e.target.value)} placeholder="Enter the Event Details..." style={{ width: "100%", boxSizing: "border-box", padding: "9px 11px", border: "1px solid #cfd8e3", borderRadius: "8px", fontSize: "14px", minHeight: "70px", resize: "vertical" }} />
                    </div>
                  </div>
                ))}
                <button type="button" className="tv-add" onClick={addItinerary} style={{ marginBottom: "16px" }}>
                  + Add Itinerary
                </button>
              </div>
            )}

            {mode === "self" && (
              <div className="tv-field tv-full">
                <label>Event Details</label>
                <textarea name="eventDetails" value={formData.eventDetails} onChange={handleChange} placeholder="Enter the Event Details..." />
              </div>
            )}
            <div className="tv-field tv-full">
              <label>Event Details</label>
              <textarea name="eventDetails" value={formData.eventDetails} onChange={handleChange} placeholder="Enter the Event Details..." />
            </div>
            <div className="tv-field tv-full">
              <label>Remarks</label>
              <textarea name="remarks" value={formData.remarks} onChange={handleChange} placeholder="Enter the Remarks..." />
            </div>
          </div>

          <div className="tv-actions">
            <button type="submit" disabled={loading}>
              {loading ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default RequestTravel;
